import {useCallback, useEffect, useMemo} from 'react';
import {debounce} from 'ts-debounce';

import {Sequence} from 'components/Sequence';
import AaSequence from 'molbio/aa-sequences/AaSequence';
import {Endpoints} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {
  BA_EMPTY_VALUE,
  baNumber,
  isBANumber,
  BulkAssemblyOrientation,
} from 'molbio/bulk-assembly/BulkAssemblyValues';
import {RestrictionEnzymeInfo} from 'molbio/bulk-assembly/util/EnzymeUtil';
import {getValueForOrientation} from 'molbio/bulk-assembly/util/FragmentEndpointUtil';
import {Selection} from 'molbio/maps/selectables/Selection';
import useEventHandler from 'ui-library/hooks/useEventHandler';

export const DEBOUNCE_DELAY_MS = 50;

interface MirroringFromPolymerMapSelectionHookArgs {
  maybeSelection: Selection | undefined;
  setEndpoints: (endpoints: Endpoints) => void;
  setRestrictionEnzymeId5: (id: number) => void;
  setRestrictionEnzymeId3: (id: number) => void;
  canUpdateRestrictionEnzymes: boolean;
}

export function useMirroringFromPolymerMapSelection({
  maybeSelection,
  setEndpoints,
  setRestrictionEnzymeId5,
  setRestrictionEnzymeId3,
  canUpdateRestrictionEnzymes,
}: MirroringFromPolymerMapSelectionHookArgs): void {
  const changeHandler = useCallback(
    (selection: Selection) => {
      const end = selection.end();
      setEndpoints({
        start: baNumber(selection.start()),
        end: end != null ? baNumber(end) : BA_EMPTY_VALUE,
      });

      if (canUpdateRestrictionEnzymes) {
        const maybeStartRestrictionEnzymeId = selection.getStartState('cutsite')?.get('enzyme').id;
        if (maybeStartRestrictionEnzymeId != null) {
          setRestrictionEnzymeId5(maybeStartRestrictionEnzymeId);
        }
        const maybeEndRestrictionEnzymeId = selection.getEndState('cutsite')?.get('enzyme').id;
        if (maybeEndRestrictionEnzymeId != null) {
          setRestrictionEnzymeId3(maybeEndRestrictionEnzymeId);
        }
      }
    },
    [setEndpoints, setRestrictionEnzymeId5, setRestrictionEnzymeId3, canUpdateRestrictionEnzymes]
  );

  // Apply debouncing to handler outside of useCallback to avoid linter issues.
  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, DEBOUNCE_DELAY_MS), [changeHandler]);
  useEventHandler(
    maybeSelection,
    'change:start change:end change:startState change:endState',
    debouncedChangeHandler
  );
}

export interface MirroringFromFragmentEndpointsHookArgs {
  polymer: null | Sequence | AaSequence;
  canUpdateRestrictionEnzymes: boolean;
  canSpecifyEndpoints: boolean;
  isReadOnly: boolean;
  restrictionEnzymeInfo5: RestrictionEnzymeInfo;
  restrictionEnzymeInfo3: RestrictionEnzymeInfo;
  endpoints: Endpoints;
  orientation: BulkAssemblyOrientation;
  canMirrorToMap: boolean;
  isConcatenation: boolean;
}

export function useMirroringFromFragmentEndpoints({
  polymer,
  canUpdateRestrictionEnzymes,
  canSpecifyEndpoints,
  isReadOnly,
  restrictionEnzymeInfo5,
  restrictionEnzymeInfo3,
  endpoints,
  orientation,
  canMirrorToMap,
  isConcatenation,
}: MirroringFromFragmentEndpointsHookArgs): void {
  useEffect(() => {
    if (polymer == null) {
      // Polymer has not loaded yet.
      return;
    }
    const selection = polymer.selection();
    const start = isBANumber(endpoints.start) ? endpoints.start.value : null;
    const end = isBANumber(endpoints.end) ? endpoints.end.value : null;
    if (canMirrorToMap && !isReadOnly && (start !== selection.start() || end !== selection.end())) {
      // NOTE: ideally, we'd call Selection.reset() here if the source of the change is the manual input elements.
      // Adding that call though breaks the "dragging" functionality if we don't keep track of the change source
      selection.setEndpoints(start, end);
    } else if (!canSpecifyEndpoints || isReadOnly) {
      if (!canSpecifyEndpoints) {
        // In methods where the user can't specify endpoints, we always look at the full sequence, so we
        // want to make it always look selected. Select the full polymer.
        selection.setEndpoints(0, polymer.length());
      }
      // Block any updates to the Selection model if the user should be unable to specify endpoints or the
      // fragment is read-only. This monkey-patches the Backbone method; ideally we would use another approach
      // like setting an attribute instead.
      selection.set = () => selection;
    }

    if (polymer instanceof Sequence) {
      const potentialStartCutSites = polymer
        .get('cutsites')
        .filter(
          (cutsite) =>
            cutsite.position() === start &&
            cutsite.get('enzyme').id === restrictionEnzymeInfo5.restrictionEnzymeId &&
            (cutsite.strand() === 0 || cutsite.strand() === getValueForOrientation(orientation, 1, -1))
        );
      if (potentialStartCutSites.length === 1) {
        selection.addStartState({cutsite: potentialStartCutSites[0]});
      } else if (selection.getStartState('cutsite') != null) {
        selection.unsetStartState('cutsite');
      }
      const potentialEndCutSites = polymer
        .get('cutsites')
        .filter(
          (cutsite) =>
            cutsite.position() === end &&
            cutsite.get('enzyme').id === restrictionEnzymeInfo3.restrictionEnzymeId &&
            (cutsite.strand() === 0 || cutsite.strand() === getValueForOrientation(orientation, 1, -1))
        );
      if (potentialEndCutSites.length === 1) {
        selection.addEndState({cutsite: potentialEndCutSites[0]});
      } else if (selection.getEndState('cutsite') != null) {
        selection.unsetEndState('cutsite');
      }
      if (
        isConcatenation ||
        (canUpdateRestrictionEnzymes &&
          restrictionEnzymeInfo5.restrictionEnzymeId == null &&
          restrictionEnzymeInfo3.restrictionEnzymeId == null)
      ) {
        polymer.get('enzymes').showAllSingleCutters();
      } else {
        polymer.get('enzymes').hideAll();
        polymer
          .get('enzymes')
          .models.filter((enzymeModel) =>
            [restrictionEnzymeInfo5.restrictionEnzymeId, restrictionEnzymeInfo3.restrictionEnzymeId].includes(
              enzymeModel.get('id')
            )
          )
          .forEach((enzymeModel) => {
            enzymeModel.setActive();
          });
      }
      polymer.get('cutsites').trigger('cutsitesChanged');
    }
  }, [
    endpoints,
    orientation,
    polymer,
    restrictionEnzymeInfo5,
    restrictionEnzymeInfo3,
    canSpecifyEndpoints,
    isReadOnly,
    canUpdateRestrictionEnzymes,
    canMirrorToMap,
    isConcatenation,
  ]);
}
